import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { withTranslation } from 'react-i18next';
import { Box } from '@paygreen/paygreen-ui';
import { H1Title, ErrorBoundary } from '../../../components';
import { useAPIError } from '../../../context';
import { getAllCharities } from '../service';
import AdminCharitiesTable from './AdminCharitiesTable';

const AdminCharities = ({ t }) => {
    const { addError } = useAPIError();
    const fetchIdRef = useRef(0);

    // we create a ref to detect if component is mounted
    const isMounted = useRef(false);

    const [isLoading, setIsLoading] = useState(true);
    const [dataCharities, setDataCharities] = useState([]);
    const [pageCount, setPageCount] = useState(0);
    const [totalItems, setTotalItems] = useState(0);

    const fetchData = useCallback(
        ({
            pageSize,
            pageIndex,
            activationFilter,
            accountFilter,
            isOnboardingCompleted,
            isReadyToOnboard,
            isAssociationValidated
        }) => {
            const fetchId = ++fetchIdRef.current;
            if (fetchId === fetchIdRef.current) {
                isMounted.current && setIsLoading(true);

                const params = {
                    limit: pageSize,
                    page: pageIndex + 1,
                    mailingStatus: activationFilter || null,
                    manager: accountFilter || null,
                    readyToOnboard: isReadyToOnboard || null,
                    onboardingCompleted: isOnboardingCompleted || null,
                    isAssociationValidated: isAssociationValidated || null,
                };

                getAllCharities(params)
                    .then((res) => {
                        const { registrations, page_count, total_items } =
                            res.data;

                        if (isMounted.current) {
                            setDataCharities(registrations);
                            setPageCount(page_count);
                            setTotalItems(total_items);
                        }
                    })
                    .catch((error) => {
                        const { status, detail } = error?.response?.data;

                        if (status === 404) {
                            isMounted.current && setDataCharities([]);
                        } else {
                            addError(detail, status);
                        }
                    })
                    .finally(() => {
                        isMounted.current && setIsLoading(false);
                    });
            }
        },
        [addError],
    );

    useEffect(() => {
        isMounted.current = true;

        return () => {
            isMounted.current = false;
        };
    }, []);

    return (
        <>
            <Helmet>
                <title>
                    {t('admin.charities.metaTitle')} - {t('main.metaTitle')}
                </title>
            </Helmet>

            <H1Title title={t('admin.charities.title')} />

            <ErrorBoundary>
                <Box marginTop="lg">
                    <AdminCharitiesTable
                        fetchData={fetchData}
                        data={dataCharities}
                        isLoading={isLoading}
                        pageCount={pageCount}
                        totalItems={totalItems}
                    />
                </Box>
            </ErrorBoundary>
        </>
    );
};

export default withTranslation('common')(AdminCharities);
